import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/layout/Layout';
import ServerStatus from '@/components/ServerStatus';
import Dashboard from '@/pages/Dashboard';
import ProfileSettings from '@/pages/ProfileSettings';
import CollegeSettings from '@/pages/settings/CollegeSettings';
import ManualCertificatePrint from '@/pages/certificates/ManualCertificatePrint';
import CertificateHistory from '@/pages/certificates/CertificateHistory';
import CertificateSettings from '@/pages/certificates/CertificateSettings';
import BackupPage from '@/pages/admin/BackupPage';
import SendNotification from '@/pages/notifications/SendNotification';
import StaffMessages from '@/pages/notifications/StaffMessages';

// Import all route modules
import {
  adminRoutes,
  masterSettingsRoutes,
  studentRoutes,
  feesRoutes,
  hrRoutes,
  libraryRoutes,
  inventoryRoutes,
  hostelRoutes,
  transportRoutes,
  consultantRoutes,
  seatBookingRoutes,
  admissionRoutes,
  attendanceRoutes,
  routineRoutes,
  examinationRoutes,
  leaveRoutes,
  accountsRoutes,
  reportsRoutes,
  frontDeskRoutes,
  communicationRoutes,
  transcriptsRoutes,
  ticketRoutes,
  roleRoutes,
  userManagementRoutes,
  lmsRoutes,
  settingsRoutes,
  placementRoutes,
  alumniRoutes,
  scholarshipRoutes,
  researchRoutes,
  mouRoutes,
  iqacRoutes,
  naacDashboardRoutes
} from "@/routes";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantApp = () => {
  const { tenantCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [serverError, setServerError] = useState(false);
  const [errorType, setErrorType] = useState('server'); // 'server' or 'network'

  useEffect(() => {
    checkTenantAuth();
  }, [tenantCode]);

  const checkTenantAuth = async () => {
    const token = localStorage.getItem('token');
    const storedTenantCode = localStorage.getItem('tenant_code');

    // If no token or wrong tenant, redirect to tenant login
    if (!token || storedTenantCode !== tenantCode) {
      navigate(`/${tenantCode}/login`);
      return;
    }

    try {
      // Verify token
      const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Get tenant info
      const tenantResponse = await axios.get(`${API_URL}/api/tenants/by-subdomain/${tenantCode}`);
      
      // Verify user belongs to this tenant
      if (userResponse.data.tenant_id !== tenantResponse.data.id) {
        localStorage.removeItem('token');
        localStorage.removeItem('tenant_code');
        navigate(`/${tenantCode}/login`);
        return;
      }

      setTenant(tenantResponse.data);
      setAuthenticated(true);
      setServerError(false);
    } catch (error) {
      console.error('Auth check failed:', error);
      
      // Check if it's a server/network error vs auth error
      if (!error.response) {
        // Network error - server unreachable
        setServerError(true);
        setErrorType('network');
        setLoading(false);
        return;
      } else if (error.response.status >= 500) {
        // Server error
        setServerError(true);
        setErrorType('server');
        setLoading(false);
        return;
      }
      
      // Auth error - redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('tenant_code');
      navigate(`/${tenantCode}/login`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show server error page if server is down
  if (serverError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className={`w-20 h-20 ${errorType === 'network' ? 'bg-orange-100' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto`}>
              <svg className={`w-10 h-10 ${errorType === 'network' ? 'text-orange-500' : 'text-red-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {errorType === 'network' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                )}
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-3">
            {errorType === 'network' ? 'No Internet Connection' : 'Server Temporarily Unavailable'}
          </h1>
          <p className="text-gray-600 mb-6">
            {errorType === 'network' 
              ? 'Please check your internet connection and try again.'
              : 'We are experiencing technical difficulties. Our team is working on it.'}
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              {errorType === 'network' 
                ? 'Check your WiFi or mobile data connection'
                : 'Our team has been notified and is working on it'}
            </p>
          </div>
          <button
            onClick={() => {
              setLoading(true);
              setServerError(false);
              checkTenantAuth();
            }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Try Again
          </button>
          <p className="text-xs text-gray-500 mt-6">
            If the problem persists, please contact support
          </p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return null;
  }

  return (
    <ServerStatus>
      <Layout tenantCode={tenantCode}>
        <Routes>
          <Route path="/" element={<Navigate to={`/${tenantCode}/dashboard`} replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="profile" element={<ProfileSettings />} />
          <Route path="college-settings" element={<CollegeSettings />} />
        
        {/* Certificate Routes */}
        <Route path="certificates/manual-print" element={<ManualCertificatePrint />} />
        <Route path="certificates/history" element={<CertificateHistory />} />
        <Route path="certificates/settings" element={<CertificateSettings />} />
        
        {/* Admin Routes */}
        {adminRoutes}
        
        {/* Master Settings Routes */}
        {masterSettingsRoutes}
        
        {/* Student Routes */}
        {studentRoutes}
        
        {/* Fees Routes */}
        {feesRoutes}
        
        {/* HR Management Routes */}
        {hrRoutes}
        
        {/* Library Routes */}
        {libraryRoutes}
        
        {/* Inventory Routes */}
        {inventoryRoutes}
        
        {/* Hostel Routes */}
        {hostelRoutes}
        
        {/* Transport Routes */}
        {transportRoutes}
        
        {/* Consultant Routes */}
        {consultantRoutes}
        
        {/* Seat Booking Routes */}
        {seatBookingRoutes}
        
        {/* Admission Routes */}
        {admissionRoutes}
        
        {/* User Management Routes */}
        {userManagementRoutes}
        
        {/* Attendance Routes */}
        {attendanceRoutes}
        
        {/* Routine Management Routes */}
        {routineRoutes}
        
        {/* Examination Routes */}
        {examinationRoutes}
        
        {/* Leave Manager Routes */}
        {leaveRoutes}
        
        {/* Accounts Routes */}
        {accountsRoutes}
        
        {/* Reports Routes */}
        {reportsRoutes}
        
        {/* Front Desk Routes */}
        {frontDeskRoutes}
        
        {/* Communication Routes */}
        {communicationRoutes}
        
        {/* Transcripts Routes */}
        {transcriptsRoutes}
        
        {/* Ticket Management Routes */}
        {ticketRoutes}
        
        {/* Role Management Routes */}
        {roleRoutes}
        
        {/* LMS Routes */}
        {lmsRoutes}
        
        {/* Settings Routes */}
        {settingsRoutes}
        
        {/* Placement Routes */}
        {placementRoutes}
        
        {/* Alumni Routes */}
        {alumniRoutes}
        
        {/* Scholarship Routes */}
        {scholarshipRoutes}
        
        {/* Faculty Research Routes */}
        {researchRoutes}
        
        {/* MoU Routes */}
        {mouRoutes}
        
        {/* IQAC Routes */}
        {iqacRoutes}
        
        {/* NAAC Dashboard Routes */}
        {naacDashboardRoutes}
        
        {/* Admin Routes - Additional */}
        <Route path="admin/backup" element={<BackupPage />} />
        
        {/* Notification Routes - Additional */}
        <Route path="notifications/send" element={<SendNotification />} />
        <Route path="notifications/staff-messages" element={<StaffMessages />} />
        
        {/* 404 Fallback */}
        <Route path="*" element={
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
            <h1 className="text-2xl font-bold text-gray-800">404 - Page Not Found</h1>
            <p className="text-gray-600 mt-2">The page you're looking for doesn't exist.</p>
          </div>
        } />
      </Routes>
    </Layout>
    </ServerStatus>
  );
};

export default TenantApp;
